/**
 * Header
 *
 * Top bar + desktop nav + optional hero + mobile nav + account/logout modals.
 * Composes: Brand (logo), Nav (desktop links), HeaderActions (phone + CTA/user).
 *
 * Props: onLoginClick (required), logoSrc?, logoAlt?, phoneNumber?, ctaText?, onCtaClick?, links?
 * Usage: <Header onLoginClick={() => openModal('auth')} />
 * Custom: <Header onLoginClick={...} logoSrc="/logo.png" logoAlt="Acme" phoneNumber="+1 555-0100" links={myLinks} />
 */

import { useEffect, useState, useRef } from 'react';
import { Icon } from '@iconify/react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import moment from 'moment';

import NotificationBell from '../NotificationBell';
import Avatar from '../ui/Avatar';
import ProfileEditModal from '../profile/ProfileEditModal';
import { supabase } from '../../lib/supabaseClient';
import { useScrollNavigation } from '../../hooks/useScrollNavigation';
import { useUserType } from '../../hooks/useUserType';
import { useUserProfile } from '../../hooks/useUserProfile';
import { normalizeAvatarUrl } from '../../hooks/useUserProfiles';

import logoImage from '../../assets/images/logo.webp';
import truckImage from '../../assets/images/truck.webp';
import { DEFAULT_PHONE, DEFAULT_NAV_LINKS } from './constants';
import type { HeaderProps } from './types';
import { Brand } from './Brand';
import { Nav } from './Nav';
import { HeaderActions } from './HeaderActions';
import { MobileNav } from './MobileNav';

export default function Header({
  onLoginClick,
  logoSrc = logoImage,
  logoAlt = 'Wincrest Logistics LLC',
  phoneNumber = DEFAULT_PHONE,
  ctaText = 'Login',
  onCtaClick,
  links = DEFAULT_NAV_LINKS,
}: HeaderProps) {
  const handleCta = onCtaClick ?? onLoginClick;
  const [user, setUser] = useState<any>(null);
  const [showLogout, setShowLogout] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [quickAccessOpen, setQuickAccessOpen] = useState(false);
  const quickAccessRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  const [topBarHeight, setTopBarHeight] = useState(85);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        quickAccessRef.current &&
        !quickAccessRef.current.contains(e.target as Node)
      ) {
        setQuickAccessOpen(false);
      }
    };
    if (quickAccessOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [quickAccessOpen]);
  const { navigateToSection } = useScrollNavigation();
  const location = useLocation();

  const { data: userProfile, refetch: refetchUserProfile } = useUserProfile(
    user?.id,
  );
  const { data: userTypeData } = useUserType(user?.id);
  const isHomePage = location.pathname === '/';
  const userRole = userProfile?.role ?? null;

  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data }) => {
      if (mounted) setUser(data.user);
    });
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (mounted) {
          setUser(session?.user ?? null);
          if (!session?.user) setShowLogout(false);
        }
      },
    );
    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const getInitials = (email: string) => {
    if (!email) return '';
    const [name] = email.split('@');
    return name
      .split(/[.\-_]/)
      .map((part) => part[0]?.toUpperCase())
      .join('')
      .slice(0, 2);
  };

  const getUserDisplayName = (): string => {
    if (userProfile?.full_name) return userProfile.full_name;
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    if (user?.user_metadata?.name) return user.user_metadata.name;
    if (user?.email) {
      const [username] = user.email.split('@');
      return username
        .split('.')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return 'User';
  };

  const getUserDisplayRole = (): string => {
    if (userRole === 'admin') return 'Admin';
    if (userRole === 'agent') return 'Agent';
    if (userTypeData?.hasCarrierRegistration && userTypeData?.hasQuotes)
      return 'Carrier-Client';
    if (userTypeData?.hasCarrierRegistration) return 'Carrier';
    if (userTypeData?.hasQuotes) return 'Client';
    return user?.email || 'User';
  };

  const getResolvedAvatarUrl = (): string | null => {
    const identityData = user?.identities?.[0]?.identity_data;
    const candidates = [
      userProfile?.avatar_url,
      user?.user_metadata?.avatar_url,
      user?.user_metadata?.picture,
      identityData?.avatar_url,
      identityData?.picture,
    ];
    for (const candidate of candidates) {
      const normalized = normalizeAvatarUrl(candidate || null);
      if (normalized) return normalized;
    }
    return null;
  };

  const handleLogout = async () => {
    try {
      (window as any).connect?.core?.terminate?.();
    } catch {
      // ignore
    }
    await supabase.auth.signOut();
    setShowLogout(false);
  };

  useEffect(() => {
    const updateTopBarHeight = () => {
      if (topBarRef.current) setTopBarHeight(topBarRef.current.offsetHeight);
    };
    const timeoutId = setTimeout(updateTopBarHeight, 0);
    updateTopBarHeight();
    window.addEventListener('resize', updateTopBarHeight);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateTopBarHeight);
    };
  }, [user, userTypeData]);

  useEffect(() => {
    if (!showLogout || !user?.id) return;
    refetchUserProfile();
  }, [showLogout, user?.id, refetchUserProfile]);

  const quickLinks: { to: string; label: string; icon: string }[] = [];
  if (user && userTypeData?.hasQuotes)
    quickLinks.push({
      to: '/my-quotes',
      label: 'My Quotes',
      icon: 'mdi:file-document-outline',
    });
  if (user && userTypeData?.hasCarrierRegistration)
    quickLinks.push({
      to: '/carrier-portal',
      label: 'Carrier Portal',
      icon: 'mdi:truck-fast',
    });
  if (user && userRole === 'admin') {
    quickLinks.push({
      to: '/admin/quotes',
      label: 'Admin Panel',
      icon: 'mdi:format-list-bulleted',
    });
    quickLinks.push({
      to: '/analytics',
      label: 'Analytics',
      icon: 'mdi:chart-line',
    });
  }

  const pathname = location.pathname.replace(/\/$/, '') || '/';
  const isActiveQuickLink = (to: string) => {
    const toNorm = to.replace(/\/$/, '') || '/';
    return (
      pathname === toNorm || (toNorm !== '/' && pathname.startsWith(toNorm))
    );
  };

  const profileOrLogin = !user ? (
    <button
      type="button"
      className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-[#fbf8f5]"
      onClick={handleCta}
    >
      {ctaText}
    </button>
  ) : (
    <div className="relative">
      <button
        type="button"
        className="flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all duration-200 cursor-pointer focus:outline-none"
        onClick={() => setShowLogout(true)}
        title={user.email}
      >
        <div className="flex flex-col items-start">
          <span className="text-sm font-semibold text-gray-900">
            {getUserDisplayName()}
          </span>
          <span
            className="text-xs text-gray-900/80 font-semibold truncate max-w-32"
            title={getUserDisplayRole()}
          >
            {getUserDisplayRole()}
          </span>
        </div>
        <Avatar
          src={getResolvedAvatarUrl()}
          name={userProfile?.full_name || getUserDisplayName()}
          email={user.email}
          size="md"
        />
      </button>
    </div>
  );

  const hasQuickLinksBar = quickLinks.length > 0;

  return (
    <>
      {/* Top bar: Logo + teléfono izquierda; perfil + Quick access debajo a la derecha */}
      <div
        id="site-header-topbar"
        ref={topBarRef}
        className="sticky top-0 z-50 hidden lg:flex flex-col bg-[#fbf8f5] shadow-sm"
      >
        <div className="flex items-start justify-between gap-4 px-4 py-2.5">
          <div className="flex flex-col gap-0.5 min-w-0">
            <Brand logoSrc={logoSrc} logoAlt={logoAlt} />
            {/* {phoneNumber && (
              <a
                href={`tel:${phoneNumber.replace(/\s/g, '')}`}
                aria-label={`Call support: ${phoneNumber}`}
                className="text-gray-900 text-base whitespace-nowrap no-underline hover:no-underline hover:text-gray-900 w-fit"
              >
                {phoneNumber}
              </a>
            )}*/}
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-1.5">
              {user && (userRole === 'admin' || userRole === 'agent') && (
                <NotificationBell className="hidden lg:block" />
              )}
              {profileOrLogin}
            </div>
            {hasQuickLinksBar && (
              <div
                className="relative inline-flex flex-col items-stretch w-full max-w-[280px]"
                ref={quickAccessRef}
              >
                <button
                  type="button"
                  onClick={() => setQuickAccessOpen((o) => !o)}
                  aria-expanded={quickAccessOpen}
                  aria-haspopup="true"
                  aria-label="Quick access menu"
                  className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg text-sm text-gray-900 font-medium bg-white/80 hover:bg-white border border-gray-900/15 shadow-sm transition-colors focus:outline-none"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Icon
                      icon="mdi:view-dashboard-outline"
                      className="w-4 h-4 flex-shrink-0"
                      aria-hidden
                    />
                    <span className="truncate">Quick access</span>
                  </span>
                  <Icon
                    icon={
                      quickAccessOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'
                    }
                    className="w-4 h-4 flex-shrink-0 transition-transform"
                    aria-hidden
                  />
                </button>
                {quickAccessOpen && (
                  <div
                    className="absolute top-full left-0 right-0 mt-1 w-full py-1 bg-white rounded-lg border border-gray-900/15 shadow-lg z-[100] max-h-80 overflow-y-auto"
                    role="menu"
                  >
                    {quickLinks.map(({ to, label, icon }) => {
                      const isActive = isActiveQuickLink(to);
                      return (
                        <Link
                          key={to}
                          to={to}
                          role="menuitem"
                          onClick={() => setQuickAccessOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 text-sm min-h-[44px] transition-colors focus:outline-none text-gray-900/80 hover:bg-gray-100 hover:text-gray-900"
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <Icon
                            icon={icon}
                            className="w-5 h-5 flex-shrink-0 text-current"
                            aria-hidden
                          />
                          {label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Nav
        links={links}
        navigateToSection={navigateToSection}
        topOffsetPx={topBarHeight}
      />

      {isHomePage && (
        <motion.header
          className="relative w-full min-h-[300px] lg:min-h-[500px] overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <img
            src={truckImage}
            alt="Truck transporting vehicle"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <HeroContent />
        </motion.header>
      )}

      <MobileNav
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        links={links}
        logoSrc={logoSrc}
        logoAlt={logoAlt}
        phoneNumber={phoneNumber}
        location={location}
        onLoginClick={onLoginClick}
        user={user}
        userRole={userRole}
        userProfile={userProfile}
        hasQuotes={Boolean(userTypeData?.hasQuotes)}
        hasCarrier={Boolean(userTypeData?.hasCarrierRegistration)}
        onAvatarClick={() => setShowLogout(true)}
        getInitials={getInitials}
        getUserDisplayName={getUserDisplayName}
        getUserDisplayRole={getUserDisplayRole}
        avatarUrl={getResolvedAvatarUrl()}
      />

      {user && userProfile && (
        <ProfileEditModal
          isOpen={showProfileEdit}
          onClose={() => setShowProfileEdit(false)}
          userId={user.id}
          currentName={userProfile.full_name || ''}
          currentEmail={user.email}
          currentPhoneNumber={userProfile.phone_number}
          currentAvatarUrl={userProfile.avatar_url}
          onSuccess={() => {}}
        />
      )}

      {showLogout && user && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="account-modal-title"
        >
          <div className="relative w-full h-[100dvh] bg-[#fbf8f5] rounded-none shadow-2xl max-h-[100dvh] overflow-y-auto overscroll-contain sm:h-auto sm:max-w-[360px] sm:max-h-[calc(100vh-2rem)] sm:rounded-2xl">
            <button
              type="button"
              onClick={() => setShowLogout(false)}
              aria-label="Close"
              className="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full text-gray-900/80 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none"
            >
              <Icon icon="mdi:close" className="w-5 h-5" />
            </button>
            <div className="bg-[#fbf8f5] px-6 pt-8 pb-6 text-center border-b border-gray-200">
              <img src={logoSrc} alt={logoAlt} className="h-20 mx-auto mb-4" />
              <h2
                id="account-modal-title"
                className="text-lg font-bold text-gray-900"
              >
                Account
              </h2>
              <p className="text-sm text-gray-900/80 mt-0.5">
                Manage your Wincrest Logistics account
              </p>
            </div>
            <div className="p-6 bg-[#fbf8f5]">
              <div className="flex flex-col items-center text-center mb-6">
                <Avatar
                  src={getResolvedAvatarUrl()}
                  name={userProfile?.full_name || getUserDisplayName()}
                  email={user.email}
                  size="xl"
                  className="ring-2 ring-white shadow-lg"
                />
                <p className="text-gray-900/80 text-xs mt-3">Signed in as</p>
                <p className="font-semibold text-gray-900 truncate max-w-full px-1">
                  {getUserDisplayName()}
                </p>
                <p className="text-sm text-gray-900/80 font-medium">
                  {getUserDisplayRole()}
                </p>
                <p className="text-xs text-gray-900/80 truncate max-w-full mt-1 px-1">
                  {user.email}
                </p>
              </div>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowLogout(false);
                    setShowProfileEdit(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors"
                >
                  <Icon icon="mdi:account-edit-outline" className="w-5 h-5" />
                  Edit profile
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                >
                  <Icon icon="mdi:logout" className="w-5 h-5" />
                  Sign out
                </button>
                <button
                  type="button"
                  onClick={() => setShowLogout(false)}
                  className="w-full py-3 px-4 rounded-xl border-2 border-gray-900/20 text-gray-900 font-semibold hover:bg-gray-100 focus:outline-none transition-colors"
                >
                  Cancel
                </button>
              </div>
              <p className="mt-5 text-center text-xs text-gray-900/80">
                Your session is secure and encrypted.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function HeroContent() {
  const yearsInBusiness = moment().diff(moment('2007-01-01'), 'years');
  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center text-center px-6">
      <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4">
        Wincrest Logistics LLC
      </h1>
      <p className="text-lg md:text-2xl lg:text-3xl font-semibold text-white mb-4">
        FREE Quotes - Safe and Reliable Car Shipping
      </p>
      <p className="text-sm md:text-lg lg:text-xl text-white">
        {yearsInBusiness} Years in Business & Over 65,000 Vehicles Safely
        Transported
      </p>
    </div>
  );
}

export { Brand, Nav, HeaderActions, MobileNav };
export type { HeaderProps, NavLinkItem } from './types';
